import React from 'react'
import { useParams } from 'react-router-dom'
import { Formik, Form } from 'formik'
import { Container, Col, Label, Row, Card, CardBody } from 'reactstrap'
import * as Yup from 'yup'
import {
  useSingleQuery,
  useAllQuery,
} from '../../utils/hooks/reactQuery/queries/queries'
import { toast } from 'react-toastify'
import {
  BootstrapTextInput,
  FormCard,
  SaveButton,
  AceCodeEditorInput,
  TypeaheadSingleInput,
  TypeaheadMultiInput,
  BootstrapToggleInput,
} from '../../components/forms'
import { useUser } from '../../utils/contexts/userContext'
import { useItemMutation } from '../../utils/hooks/reactQuery/mutations'
import axios from 'axios'
import { apiUrl } from '../../utils/url'
import { getJwt } from '../../utils/jwt'

const IterableTemplateSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  fromEmail: Yup.string().required('From Email is required'),
  fromName: Yup.string().required('From Name is required'),
  subject: Yup.string().required('Subject Line is required'),
  html: Yup.string().required('Content is required'),
  clientTemplateId: Yup.string().required('Client Template ID is required'),
  messageTypeId: Yup.string().required('Message Type is required'),
  creatorUserId: Yup.string().required('Email is required'),
  dataFeedIds: Yup.array().of(Yup.number()).optional(),
  cacheDataFeed: Yup.boolean().optional(),
  mergeDataFeedContext: Yup.boolean().optional(),
})

export default function IterableTemplate() {
  const { user } = useUser()
  const { id } = useParams()
  const { data, isLoading, isError, error } = useSingleQuery(
    'iterable-template',
    id,
  )
  const { data: messageTypes = [] } = useAllQuery('iterable-message-types')
  const { data: dataFeeds = [] } = useAllQuery('iterable-data-feeds')
  const { update, add } = useItemMutation('iterable-template', 'templateId')
  const getApprovedSender = async (value, setFieldValue) => {
    const message = messageTypes.find((messageType) => messageType.id === value)
    const options = {
      method: 'GET',
      url: `${apiUrl}iterable-approved-senders?channelId=${message.channelId}`,
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
    const { data } = await axios.request(options)
    setFieldValue('fromEmail', data.approvedSender)
    setFieldValue('messageTypeId', value)
  }
  const handleSubmit = (values, { setSubmitting }) => {
    const toastId = toast.loading('Saving template file...')

    // Are we updating or adding?
    if (id !== 'new') {
      update.mutate(
        {
          type: 'Email',
          template: { ...values, creatorUserId: user.username },
        },
        {
          onSuccess: () => {
            toast.update(toastId, {
              render: 'Saved Iterable template file!',
              type: 'success',
              isLoading: false,
              autoClose: 1500,
            })
          },
          onError: (error) => {
            console.error(error)
            toast.update(toastId, {
              render: 'Could not save Iterable template file...',
              type: 'error',
              isLoading: false,
              autoClose: 1500,
            })
            setSubmitting(false)
          },
        },
      )
    } else {
      add.mutate(
        {
          type: 'Email',
          template: { ...values, creatorUserId: user.username },
        },
        {
          onSuccess: () => {
            toast.update(toastId, {
              render: 'Saved Iterable template file!',
              type: 'success',
              isLoading: false,
              autoClose: 1500,
            })
          },
          onError: (error) => {
            console.error(error)
            toast.update(toastId, {
              render: 'Could not save Iterable template file...',
              type: 'error',
              isLoading: false,
              autoClose: 1500,
            })
            setSubmitting(false)
          },
        },
      )
    }
  }

  return (
    <Container fluid>
      {isLoading ? (
        <h5>Loading...</h5>
      ) : isError ? (
        <h5>Error: {error.message}</h5>
      ) : (
        <FormCard
          breadcrumbLink="/dashboard/iterable-templates"
          breadcrumbLabel="Iterable Template Files"
          breadcrumbItem={
            data.name.length > 0 ? data.name : 'New Iterable Template File'
          }
        >
          <Formik
            initialValues={{ ...data, creatorUserId: user?.username }}
            validationSchema={IterableTemplateSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, isSubmitting, isValid, setFieldValue }) => (
              <Form>
                <Row>
                  <BootstrapTextInput
                    fullWidth
                    label="Name"
                    id="name"
                    name="name"
                    placeholder="Name"
                  />
                  <BootstrapTextInput
                    label="From Name"
                    id="fromName"
                    name="fromName"
                    placeholder="From Name"
                  />
                  <BootstrapTextInput
                    label="Subject Line"
                    id="subject"
                    name="subject"
                    placeholder="Subject Line"
                  />
                  <TypeaheadSingleInput
                    label="Message Type"
                    id="messageTypeId"
                    name="messageTypeId"
                    placeholder="Message Type"
                    objects
                    labelKey="name"
                    valueKey="id"
                    customOnChange={(value) => {
                      getApprovedSender(value, setFieldValue)
                    }}
                    options={messageTypes}
                  />
                  <TypeaheadMultiInput
                    label="Data Feeds"
                    id="dataFeedIds"
                    name="dataFeedIds"
                    placeholder="Data Feeds"
                    objects
                    labelKey="name"
                    valueKey="id"
                    options={dataFeeds}
                  />
                  {values.dataFeedIds.length > 0 && (
                    <>
                      <BootstrapToggleInput
                        label="Cache Data Feed"
                        id="cacheDataFeed"
                        name="cacheDataFeed"
                        labelChecked="Data feed will be cached."
                        labelUnchecked="Data feed will not be cached."
                      />
                      <BootstrapToggleInput
                        label="Merge Data Feed Context"
                        id="mergeDataFeedContext"
                        name="mergeDataFeedContext"
                        labelChecked="Data feed will be merged with user context."
                        labelUnchecked="Data feed will not be merged with user context."
                      />
                    </>
                  )}
                </Row>
                <Row>
                  <Col md={6} className="border-end border-dark">
                    <AceCodeEditorInput label="Content" id="html" name="html" />
                  </Col>
                  <Col md={6}>
                    <Label for="previewIframe">Preview</Label>
                    <Card className="border-0" id="previewIframe">
                      <CardBody className="p-0">
                        <iframe
                          title="Template Preview"
                          srcDoc={values.html}
                          style={{
                            width: '100%',
                            height: '100%',
                            minHeight: '500px',
                            border: 'none',
                          }}
                          sandbox="allow-scripts allow-same-origin"
                        />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <SaveButton disabled={isSubmitting || !isValid} />
              </Form>
            )}
          </Formik>
        </FormCard>
      )}
    </Container>
  )
}
